import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'

export default function Pricing() {
  const { t } = useTranslation()

  const plans = [
    {
      name: t('pricing.basic.name'),
      price: t('pricing.basic.price'),
      description: t('pricing.basic.description'),
      features: [
        t('pricing.basic.features.f1'),
        t('pricing.basic.features.f2'),
        t('pricing.basic.features.f3'),
        t('pricing.basic.features.f4')        
      ],
      cta: t('pricing.basic.cta')
    },
    {
      name: t('pricing.professional.name'),
      price: t('pricing.professional.price'),
      description: t('pricing.professional.description'),
      features: [
        t('pricing.professional.features.f1'),
        t('pricing.professional.features.f2'),
        t('pricing.professional.features.f3'),
        t('pricing.professional.features.f4'),
        t('pricing.professional.features.f5'),
        t('pricing.professional.features.f6')
      ],
      cta: t('pricing.professional.cta'),
      popular: true
    },
    {
      name: t('pricing.enterprise.name'),
      price: t('pricing.enterprise.price'),
      description: t('pricing.enterprise.description'),
      features: [
        t('pricing.enterprise.features.f1'),
        t('pricing.enterprise.features.f2'),
        t('pricing.enterprise.features.f3'),
        t('pricing.enterprise.features.f4'),
        t('pricing.enterprise.features.f5'),
        t('pricing.enterprise.features.f6'),
        t('pricing.enterprise.features.f7')
      ],
      cta: t('pricing.enterprise.cta')
    }
  ]

  return (
    <section id="pricing" className="py-20 relative overflow-hidden">
      {/* Background */}
      <div 
        className="absolute inset-0"
        style={{
          backgroundImage: `url('/images/temp/slide2.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          zIndex: -1
        }}
      />
      {/* Overlay */}
      <div 
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.5))',
          zIndex: -1
        }}
      />
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <motion.h2 
            className="text-4xl font-bold mb-4 text-white"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            {t('pricing.title')}
          </motion.h2>
          <motion.p 
            className="text-xl text-gray-300"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {t('pricing.subtitle')}
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {plans.map((plan, index) => (
            <motion.div
              key={plan.name}
              className={`rounded-lg overflow-hidden ${
                plan.popular 
                  ? 'bg-white shadow-xl border-2 border-[#ff5f00] relative' 
                  : 'bg-white shadow-lg'
              }`}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.2 }}
            >
              {plan.popular && (
                <div className="absolute top-0 right-0 bg-[#ff5f00] text-white px-4 py-1 rounded-bl-lg">
                  {t('pricing.popularLabel')}
                </div>
              )}
              
              <div className="p-6">
                <h3 className="text-xl font-bold mb-3">{plan.name}</h3>
                <div className="mb-3">
                  <span className="text-3xl font-bold">{plan.price}</span>
                  <span className="text-gray-600 ml-2 text-sm">{t('pricing.perMonth')}</span>
                </div>
                <p className="text-gray-600 mb-4 text-sm">{plan.description}</p>
                
                <ul className="space-y-3 mb-6 text-sm">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-center text-gray-600">
                      <svg
                        className="w-5 h-5 text-[#ff5f00] mr-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>

                <button
                  className="w-full py-3 px-6 rounded-lg transition-colors bg-[#ff5f00] text-white hover:bg-[#ff4500]"
                >
                  {plan.cta}
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}
